import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { DotsIcon } from '@/components/icons';
import { IconButton, cx } from '@/components/ui';

/**
 * The "more" menu a card keeps its rarer actions under — duplicate, remove —
 * beside the arrows it has room for. Shared by the product page's blocks and
 * the home page's sections, which are the same kind of list.
 */

export interface MenuItem {
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

/** A "more" button and the few actions under it — what a card has room for beside its arrows. */
export function Menu({ label, items, bordered = false }: { label: string; items: MenuItem[]; bordered?: boolean }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useDismiss(open, root, () => setOpen(false));

  return (
    <div ref={root} className="relative">
      <IconButton
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className={cx('size-8', !bordered && 'border-0')}
      >
        <DotsIcon className="size-4" />
      </IconButton>
      {open ? (
        <div
          role="menu"
          className="animate-pop absolute end-0 top-full z-30 mt-1.5 min-w-52 rounded-panel border border-line bg-surface p-1.5 shadow-float"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className={cx(
                'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-start text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent [&>svg]:size-4 [&>svg]:shrink-0',
                item.danger ? 'text-danger hover:bg-danger/[0.07]' : 'text-ink hover:bg-ink/[0.05] [&>svg]:text-ink-faint',
              )}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Closes a popover on a press outside it or on Escape. */
export function useDismiss(open: boolean, root: RefObject<HTMLElement | null>, close: () => void) {
  useEffect(() => {
    if (!open) return undefined;
    const onPress = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPress);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPress);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, root, close]);
}
